import { useTheme } from '@/shared/context'
import {
  type ThemeMode,
  applyCssVars,
  buildAntdTheme,
  buildTintCssVars,
  deriveSurfaceTint,
  getRadiusPx,
  hueOf,
  isHexColor,
  presetSurfaces,
  readableOn,
  themeFrom,
  themeTokens,
  useLayoutSettings,
} from '@/shared/ui'
// ds-guard-ignore: provider cấu hình AntD cần ConfigProvider và App trực tiếp
import { App as AntApp, ConfigProvider } from 'antd'
import viVN from 'antd/locale/vi_VN'
import { useEffect } from 'react'

const appLocale = {
  ...viVN,
  Pagination: {
    ...viVN.Pagination,
    jump_to: 'Đến trang',
    page: '',
  },
}

function GlobalThemeConfig({ ...rest }) {
  const { theme, themeColor } = useTheme()
  const { settings } = useLayoutSettings()
  const mode = theme as ThemeMode

  const customPrimary = isHexColor(settings.customPrimary) ? settings.customPrimary : ''
  const { activePreset } = settings
  const surfaces = customPrimary
    ? (presetSurfaces(activePreset, mode) ?? deriveSurfaceTint(customPrimary, mode) ?? undefined)
    : undefined

  useEffect(() => {
    const root = document.documentElement
    applyCssVars(root, mode, hueOf(themeColor))
    const legacy = customPrimary ? themeFrom(customPrimary) : themeTokens(themeColor)
    if (legacy) {
      root.style.setProperty('--color-primary', legacy.colorPrimary)
      root.style.setProperty('--color-info', legacy.colorInfo)
    }
    if (customPrimary) {
      // Màu custom: ghi đè bộ accent (biến thể sinh từ chính màu này) và màu chữ trên accent.
      const b = legacy.components.Button
      root.style.setProperty('--vbdt-color-accent-base', customPrimary)
      root.style.setProperty('--vbdt-color-accent-hover', b.colorPrimaryHover)
      root.style.setProperty('--vbdt-color-accent-active', b.colorPrimaryActive)
      root.style.setProperty(
        '--vbdt-color-accent-bg',
        mode === 'dark'
          ? `color-mix(in srgb, ${customPrimary} 20%, transparent)`
          : legacy.components.Menu.itemSelectedBg
      )
      root.style.setProperty('--vbdt-color-text-on-accent', readableOn(customPrimary))
      // Nền trang/sider/header/thẻ/hover ngả theo màu chủ đạo (sáng và tối).
      for (const [key, value] of Object.entries(buildTintCssVars(customPrimary, mode, surfaces))) {
        root.style.setProperty(key, value)
      }
    }
  }, [mode, themeColor, customPrimary, surfaces])

  return (
    <ConfigProvider
      theme={buildAntdTheme(mode, themeColor, {
        radius: getRadiusPx(settings.radiusLevel),
        compact: settings.compact,
        customPrimary,
        surfaces,
      })}
      locale={appLocale}
    >
      <AntApp>{rest.children}</AntApp>
    </ConfigProvider>
  )
}

export default GlobalThemeConfig
